import { useRouter } from "next/router";
import Layout from "../../components/Layout";
import Head from "next/head";
import axiosInstance from "../../config/axios";
import { useEffect, useState } from "react";
import { loader } from "../../store/actions/loader";
import InlineLoading from "../../components/UI/inlineLoader";
import { useDispatch, useSelector } from "react-redux";
import useGAEventsTracker from "../../helpers/useGAEventsTracker";
import { NotificationManager } from "react-notifications";
import { addToCart, setTotalPrice } from "../../store/actions/shop";
import ProductCard from "../../components/ProductCard";
import Router from "next/router";

// ── Quantity Selector ─────────────────────────────────────────────────────────
function QuantitySelector({ quantity, inCart, onDecrease, onIncrease, onDelete }) {
  return (
    <section>
      <div className="d-flex align-items-center qty-wrapper">
        <button
          type="button"
          onClick={inCart && quantity <= 1 ? onDelete : onDecrease}
          className="qty-btn"
          title={inCart && quantity <= 1 ? "Remove from cart" : "Decrease quantity"}
        >
          {inCart && quantity <= 1 ? (
            <i className="fa fa-trash" style={{ color: "#DC042A", fontSize: "12px" }} />
          ) : (
            <span className="qty">−</span>
          )}
        </button>

        <span style={{ minWidth: "24px", textAlign: "center", fontWeight: "600", fontSize: "16px" }}>
          {quantity}
        </span>

        <button type="button" onClick={onIncrease} className="qty-btn" title="Increase quantity">
          <span className="qty">+</span>
        </button>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
const SingleProduct = ({
  allData,
  description,
  productName,
  productPrice,
  productImage,
  productId,
  productStatus,
  relatedProducts,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const restaurant = useSelector((state) => state.shop.selectedRestaurant);
  const allCart = useSelector((state) => state.shop.cart);
  const GAEventsTracker = useGAEventsTracker("Single Product Page");

  const [productCart, setProductCart] = useState(allCart);
  const [quantity, setQuantity] = useState(1);
  const [inlineLoading, setInlineLoading] = useState(false);

  // build a product object that matches the cart shape used elsewhere
  const prod = {
    id: productId,
    product: productName,
    product_type: "simple",
    price: productPrice,
    sale_price: null,
    image_url: productImage,
    status: productStatus !== "unavailable",
  };
  
  const canonicalUrl = `https://kilimanjaro-restaurants.com/product/${router.query.name || ''}`;

   const inCart = allCart.some((cart) => cart.product?.id === productId);
const cartIndex = allCart.findIndex((cart) => cart.product?.id === productId);


const cartItem = allCart[cartIndex];
const displayQuantity = inCart ? cartItem?.quantity ?? quantity : quantity;




const handleDecrease = () => {
  if (inCart) {
    if (displayQuantity <= 1) {
      deleteFromCartHandler();
      return;
    }
    // update cart quantity directly
    const cartCopy = [...allCart];
    const price = cartCopy[cartIndex].salePrice || cartCopy[cartIndex].price;
    cartCopy[cartIndex] = {
      ...cartCopy[cartIndex],
      quantity: displayQuantity - 1,
      totalPrice: (displayQuantity - 1) * price,
    };
    setProductCart(cartCopy);
    dispatch(addToCart(cartCopy));
    dispatch(setTotalPrice());
    return;
  }
  setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
};

const handleIncrease = () => {
  if (inCart) {
    // update cart quantity directly
    const cartCopy = [...allCart];
    const price = cartCopy[cartIndex].salePrice || cartCopy[cartIndex].price;
    cartCopy[cartIndex] = {
      ...cartCopy[cartIndex],
      quantity: displayQuantity + 1,
      totalPrice: (displayQuantity + 1) * price,
    };
    setProductCart(cartCopy);
    dispatch(addToCart(cartCopy));
    dispatch(setTotalPrice());
    return;
  }
  setQuantity((prev) => prev + 1);
};

  const addtoCartHandler = () => {
    GAEventsTracker("Added to cart", `Product Name: ${productName}`);
    
    setInlineLoading(true);
    dispatch(loader());

    const prevCart = [...allCart];
    const prodInCartIndex = prevCart.findIndex((cart) => cart.product?.id === productId);

    if (prodInCartIndex >= 0) {
      const prodInCart = prevCart[prodInCartIndex];
      prevCart[prodInCartIndex] = {
        product: prod,
        quantity: prodInCart.quantity + quantity,
        price: productPrice,
        salePrice: null,
        totalPrice: prodInCart.totalPrice + quantity * productPrice,
      };
    } else {
      prevCart.push({
        product: prod,
        quantity,
        price: productPrice,
        salePrice: null,
        totalPrice: quantity * productPrice,
      });
    }

    setProductCart(prevCart);
    dispatch(addToCart(prevCart));
    dispatch(setTotalPrice());

    setTimeout(() => { dispatch(loader()); }, 1500);
    setTimeout(() => { setInlineLoading(false); }, 1500);
    setTimeout(() => { NotificationManager.success("Added to cart successfully", "", 3000); }, 1500);
  };

  const deleteFromCartHandler = () => {
    const cartSlice = [...allCart];
    cartSlice.splice(cartIndex, 1);
    setProductCart(cartSlice);
    dispatch(addToCart(cartSlice));
    dispatch(setTotalPrice());
  };

  useEffect(() => {
    const handlePopState = () => {
      const previousPath = sessionStorage.getItem("previousPath");
      if (previousPath?.startsWith("/product") && window.location.pathname === "/menu") {
        sessionStorage.setItem("navigationSource", "internal");
      }
    };
    sessionStorage.setItem("previousPath", router.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [router.pathname]);

  const isUnavailable = productStatus === "unavailable";

  return (
    <Layout>
      <Head>
      <link rel="canonical" href={canonicalUrl} />
        <title>Product page | Kilimanjaro</title>
        <meta name="description" content={description} />
        <meta name="keyword" content="MEALS FOOD RESTAURANT NIGERIA" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              name: productName,
              image: productImage,
              description,
              sku: productImage,
              brand: { "@type": "Brand", name: "Kilimanjaro" },
              offers: {
                "@type": "Offer",
                price: productPrice,
                priceCurrency: "NGN",
                availability: isUnavailable
                  ? "https://schema.org/OutOfStock"
                  : "https://schema.org/InStock",
                seller: { "@type": "Organization", name: "Kilimanjaro" },
              },
            }),
          }}
        />
      </Head>

      <h1 className="visually-hidden">{productName}</h1>

      <div className="container mb-5 px-5" style={{ marginTop: 100 }}>
        <div className="row mx-5">
          {/* Image */}
          <div className="col-md-6 mb-5" >
            <img src={productImage} alt={productName} width="100%"  style={{borderRadius: "5px"}}/>
          </div>

          {/* Details */}
          <div className="col-md-6 pl-5">

            {/* Row 1: Product name + quantity selector */}
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-5">
              <h5 className="product-name mb-0">{productName}</h5>
              <QuantitySelector
                quantity={displayQuantity}
                inCart={inCart}
                onDecrease={handleDecrease}
                onIncrease={handleIncrease}
                onDelete={deleteFromCartHandler}
              />
            </div>

            {/* Row 2: Price + Add to Cart button */}
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-4 ">
              <h5 className="mb-0" style={{ fontWeight: "700" }}>
                ₦{new Intl.NumberFormat().format(productPrice)}
              </h5>

              {isUnavailable ? (
                <button className="btn" disabled style={{ opacity: 0.6, cursor: "not-allowed" }}>
                  <span className="text">Out of Stock</span>
                </button>
              ) : inlineLoading ? (
                <InlineLoading />
              ) : inCart ? (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  color: "#E1686D",
                  fontWeight: "600",
                  fontSize: "14px",
                }}>
                  <span style={{ fontSize: "20px" }}>✓</span> Added to Cart
                </span>
              ) : (
                <button className="btn" onClick={addtoCartHandler}>
                  <span className="text">Add to Cart</span>
                </button>
              )}
            </div>

            <p>{description}</p>
              <div className="d-flex justify-content-center justify-content-md-start">
                <button
              className="btn"
              onClick={() => {
                sessionStorage.setItem("navigationSource", "internal");
                Router.push(`/menu/${restaurant.slug}`);
              }}
            >
              <span className="text">Back to Menu</span>
            </button>
              </div>
            
          </div>
        </div>

        {/* Related products */}
        <h5 className="mt-5 w-100 text-center text-md-left">Related products</h5>
        <div className="row px-5" style={{ marginTop: 30 }}>
          {relatedProducts.map((item, index) => (
            <div
              key={index}
              className="col-md-6 col-12 mb-5"
              style={{ paddingRight: index % 2 === 0 ? 30 : 5 }}
            >
              <ProductCard item={item} />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const { name } = context.query;
  let { data } = await axiosInstance.get(`V1/products/${name}`);
  

  return {
    props: {
      allData: data.data.product,
      description: data.data.product.product_description,
      productName: data.data.product.name,
      productPrice: data.data.product.price,
      productImage: data.data.product.image,
      productId: data.data.product.product_id,
      productStatus: data.data.product.status,  // ← now passed through
      relatedProducts: data.data.related_products,
    },
  };
}

export default SingleProduct;